import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStripe, useElements, PaymentElement, AddressElement } from '@stripe/react-stripe-js';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import NooderaModal from 'components/NooderaModal';

import { useIsUniversityAdmin } from 'utils/customHooks';
import {
  useCreateOrUpdateOwnUserStripeCustomerMutation,
  useGetOwnUserStripeCustomerQuery,
} from 'lib/Api';

import {
  paymentResetUserPurchaseFormStep,
  paymentSetFormInvoice,
  paymentSetUserPurchaseFormStep,
} from 'ducks/payment/reducer';

import { InvoiceInterface } from 'commons/types/stripeTypes';
import { ShoppingCartInvoiceInterface } from 'commons/types/shoppingCartTypes';
import { CreateStripeCourseInvoiceResponse } from 'commons/types/paymentTypes';
import { FormContainer, Wrapper, StyledCheckoutNooderaButton } from './CheckoutForm.styles';

import { HeaderTitle } from '../Invoice.styles';

interface CheckoutFormProps {
  buttonText?: string;
  customErrorHandling?: () => any;
  customPadding?: string;
  invoice?: InvoiceInterface;
  isNested?: boolean;
  maxWidth?: string;
  onSuccess: (() => void) | (() => Promise<void>);
  onCreateInvoice?: (
    stripeCustomerId: string,
  ) => Promise<CreateStripeCourseInvoiceResponse | ShoppingCartInvoiceInterface>;
}

interface UserPurchaseCheckoutFormProps extends CheckoutFormProps {
  publisherPartner?: {
    partnerId?: string;
    stripeAccountId?: string;
  };
}

interface CheckoutFormSubcomponents {
  UserPurchase: React.FC<UserPurchaseCheckoutFormProps>;
}

const CheckoutForm: React.FC<CheckoutFormProps> & CheckoutFormSubcomponents = ({
  buttonText = null,
  customErrorHandling = null,
  customPadding = null,
  invoice,
  isNested = false,
  maxWidth = null,
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const isUniversityAdmin = useIsUniversityAdmin();
  const [isLoading, setIsLoading] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [isStripeLoading, setIsStripeLoading] = React.useState(true);
  const [totalSeats, setTotalSeats] = React.useState(0);
  const [alertValues, setAlertValues] = React.useState({
    icon: null,
    title: '',
    description: '',
    onBtnClick: null,
    btnText: '',
  });
  const userEmail = useSelector((state) => state.user.email);

  React.useEffect(() => {
    if (elements) {
      const element = elements.getElement('payment');
      element.on('ready', () => {
        setIsStripeLoading(false);
      });
    }
  }, [elements]);

  React.useEffect(() => {
    if (invoice?.lines?.data.length > 0) {
      const totalQty = invoice.lines.data.reduce(
        (accumulator: Number, lineItem: any) => accumulator + lineItem.quantity,
        0,
      );
      setTotalSeats(totalQty);
    }
  }, []);

  const onContinue = (): void => {
    onSuccess();
    setShowDialog(false);
  };

  const onClose = (): void => {
    setShowDialog(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const addressElement = elements.getElement('address');
    const { complete, value } = await addressElement.getValue();

    if (!stripe || !elements) {
      return;
    }

    if (!complete) {
      return;
    }

    setIsLoading(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        payment_method_data: {
          billing_details: {
            ...value,
            email: userEmail,
          },
        },
      },
      redirect: 'if_required',
    });

    if (error) {
      if (customErrorHandling) {
        customErrorHandling();
      } else {
        setAlertValues({
          icon: <ErrorOutlineIcon />,
          title: 'Oops, something happened!',
          description:
            'There was an error processing your payment. Please try again later or use a different payment method!',
          btnText: 'Close',
          onBtnClick: () => onClose(),
        });
        setShowDialog(true);
      }
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      if (isNested) {
        onSuccess();
      } else {
        const title = isUniversityAdmin ? 'Successful Purchase!' : 'Successful Payment!';
        const description = isUniversityAdmin
          ? `Congratulations! You've successfully added ${totalSeats} seats to ${invoice?.metadata?.childPartnerName}. The instance is now ready to accommodate more users.`
          : `Congratulations! You've successfully paid this invoice.`;
        setAlertValues({
          icon: <CheckCircleOutlineIcon />,
          title,
          description,
          onBtnClick: () => onContinue(),
          btnText: 'Continue',
        });
        setShowDialog(true);
      }
    }
    setIsLoading(false);
  };

  return (
    <FormContainer onSubmit={handleSubmit} maxWidth={maxWidth} customPadding={customPadding}>
      <Wrapper aria-live='polite' maxWidth={maxWidth}>
        <HeaderTitle>Payment Information</HeaderTitle>
        <PaymentElement
          options={{
            fields: {
              billingDetails: {
                address: {
                  country: 'never',
                },
              },
            },
          }}
        />
      </Wrapper>
      {!isStripeLoading && (
        <>
          <Wrapper maxWidth={maxWidth}>
            <HeaderTitle>Billing Information</HeaderTitle>
            <AddressElement options={{ mode: 'billing' }} />
          </Wrapper>
          <NooderaModal.Small isOpen={showDialog} {...alertValues} />
          <StyledCheckoutNooderaButton type='submit' isDisabled={isLoading}>
            {buttonText || `Pay ${(invoice?.total / 100).toFixed(2)}`}
          </StyledCheckoutNooderaButton>
        </>
      )}
    </FormContainer>
  );
};

const UserPurchaseCheckoutForm: React.FC<UserPurchaseCheckoutFormProps> = ({
  buttonText = null,
  customErrorHandling = null,
  customPadding = null,
  invoice,
  isNested = false,
  maxWidth = null,
  publisherPartner = null,
  onSuccess,
  onCreateInvoice,
}) => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const isUniversityAdmin = useIsUniversityAdmin();
  const [createOrUpdateOwnUserStripeCustomer] = useCreateOrUpdateOwnUserStripeCustomerMutation();

  const { data: userStripeCustomerData, isLoading: isUserStripeCustomerDataLoading } =
    useGetOwnUserStripeCustomerQuery(publisherPartner ? publisherPartner?.partnerId : null);

  const { step: formStep } = useSelector((state) => state.payment.paymentForm);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showDialog, setShowDialog] = React.useState<boolean>(false);
  const [isStripeLoading, setIsStripeLoading] = React.useState<boolean>(true);
  const [alertValues, setAlertValues] = React.useState({
    icon: null,
    title: '',
    description: '',
    onBtnClick: null,
    btnText: '',
  });

  const userEmail = useSelector((state) => state.user.email);

  const defaultValues = React.useMemo(
    () => ({
      name: userStripeCustomerData?.name ?? '',
      email: userStripeCustomerData?.email ?? '',
      phone: userStripeCustomerData?.phone ?? '',
      address: {
        line1: userStripeCustomerData?.address?.line1 ?? '',
        line2: userStripeCustomerData?.address?.line2 ?? '',
        city: userStripeCustomerData?.address?.city ?? '',
        state: userStripeCustomerData?.address?.state ?? '',
        postal_code: userStripeCustomerData?.address?.postalCode ?? '',
        country: userStripeCustomerData?.address?.country ?? '',
      },
    }),
    [userStripeCustomerData],
  );

  React.useEffect(() => {
    // Reset form step on component mount and dismount
    dispatch(paymentResetUserPurchaseFormStep());
    dispatch(paymentSetFormInvoice(null));

    return () => {
      dispatch(paymentResetUserPurchaseFormStep());
      dispatch(paymentSetFormInvoice(null));
    };
  }, []);

  React.useEffect(() => {
    if (elements) {
      const element = elements.getElement('payment');
      element.on('ready', () => {
        setIsStripeLoading(false);
      });
    }
  }, [elements]);

  const handleContinue = (): void => {
    setShowDialog(false);
    // Reset form values on successful payment
    dispatch(paymentResetUserPurchaseFormStep());
    dispatch(paymentSetFormInvoice(null));
  };

  const handleClose = (): void => {
    setShowDialog(false);
    // Reset form values on successful payment
    dispatch(paymentResetUserPurchaseFormStep());
    dispatch(paymentSetFormInvoice(null));
  };

  const handleCreateInvoice = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const addressElement = elements.getElement('address');
    const { complete, value } = await addressElement.getValue();

    if (!stripe || !elements) {
      return;
    }

    if (!complete) {
      return;
    }

    let customerData;

    try {
      setIsLoading(true);
      customerData = await createOrUpdateOwnUserStripeCustomer({
        email: userEmail,
        name: value.name,
        address: {
          line1: value.address.line1,
          line2: value.address.line2,
          city: value.address.city,
          state: value.address.state,
          postalCode: value.address.postal_code,
          country: value.address.country,
        },
        stripeAccountPartnerId: publisherPartner?.partnerId,
      }).unwrap();
    } catch (err) {
      setShowDialog(true);
      setAlertValues({
        icon: <ErrorOutlineIcon />,
        title: 'Invalid Address!',
        description:
          "We couldn't verify your address. Please check your address and try again. If the problem persists, please contact support.",
        btnText: 'Close',
        onBtnClick: () => handleClose(),
      });

      setIsLoading(false);
    }

    if (customerData) {
      try {
        const response = await onCreateInvoice(customerData.id);

        // Set invoice id in redux store so the parent component can display the summary
        // and we can sync the payment methods to stripe elements.
        dispatch(paymentSetFormInvoice(response?.invoice?.id));
        dispatch(paymentSetUserPurchaseFormStep(2));

        setIsLoading(false);
      } catch (err) {
        setShowDialog(true);
        setAlertValues({
          icon: <ErrorOutlineIcon />,
          title: 'Server Error',
          description:
            "We couldn't create your invoice. Please contact support if the problem persists.",
          btnText: 'Close',
          onBtnClick: () => handleClose(),
        });

        setIsLoading(false);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const addressElement = elements.getElement('address');
    const { complete, value } = await addressElement.getValue();

    if (!stripe || !elements) {
      return;
    }

    if (!complete) {
      return;
    }

    setIsLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.error('🚀 ~ handleSubmit ~ submitError:', submitError);
      setIsLoading(false);
      return;
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret: invoice?.paymentIntent?.clientSecret,
      confirmParams: {
        payment_method_data: {
          billing_details: {
            ...value,
            email: userEmail,
          },
        },
      },
      redirect: 'if_required',
    });

    if (error) {
      if (customErrorHandling) {
        customErrorHandling();
      } else {
        setAlertValues({
          icon: <ErrorOutlineIcon />,
          title: 'Oops, something happened!',
          description:
            'There was an error processing your payment. Please try again later or use a different payment method!',
          btnText: 'Close',
          onBtnClick: () => handleClose(),
        });
        setShowDialog(true);
      }
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      if (isNested) {
        // ENROLL NOW isNested is true
        onSuccess();
      } else {
        // SHOPPING CART CHECKOUT
        const title = isUniversityAdmin ? 'Successful Purchase!' : 'Successful Payment!';
        await onSuccess();
        setAlertValues({
          icon: <CheckCircleOutlineIcon />,
          title,
          description: "You've successfully completed this purchase!",
          onBtnClick: handleContinue,
          btnText: 'Continue',
        });
        setShowDialog(true);
      }
      setIsLoading(false);
    }

    setIsLoading(false);
  };

  return (
    <FormContainer
      onSubmit={formStep === 1 ? handleCreateInvoice : handleSubmit}
      maxWidth={maxWidth}
      customPadding={customPadding}
    >
      <Wrapper aria-live='polite' maxWidth={maxWidth} hide={formStep === 1}>
        <HeaderTitle>Payment Information</HeaderTitle>
        <PaymentElement
          options={{
            fields: {
              billingDetails: {
                address: {
                  country: 'never',
                },
              },
            },
          }}
        />
      </Wrapper>
      {(!isStripeLoading || !isUserStripeCustomerDataLoading) && (
        <>
          <Wrapper maxWidth={maxWidth} hide={formStep === 2}>
            <HeaderTitle>Billing Information</HeaderTitle>
            <AddressElement options={{ mode: 'billing', defaultValues }} />
          </Wrapper>
          <NooderaModal.Small isOpen={showDialog} {...alertValues} />
          <StyledCheckoutNooderaButton type='submit' isDisabled={isLoading}>
            {formStep === 1 ? 'Review and Pay' : buttonText}
          </StyledCheckoutNooderaButton>
        </>
      )}
    </FormContainer>
  );
};

CheckoutForm.UserPurchase = UserPurchaseCheckoutForm;

export default CheckoutForm;
